import { useState } from "react";
import { Button, Drawer, Spin, message } from "antd";
import {
  useCurrentSystemVersionQuery,
  useSystemVersionsQuery,
  useCreateSystemVersionMutation,
  useUpdateSystemVersionMutation,
  useDeleteSystemVersionMutation,
  useRunMigrationsMutation,
  useRunSeedsMutation,
} from "../../../../Services";
import { APP_VERSION } from "../../../../Constant";
import { SystemVersion } from "../../../../Api";
import { VersionOverviewCard } from "./VersionOverviewCard";
import { CurrentVersionCard } from "./CurrentVersionCard";
import { VersionHistoryTable } from "./VersionHistoryTable";
import { VersionModal } from "./VersionModal";

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value.replace(" ", "T"));
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export const SystemVersionDrawer = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { data: currentVersionData, isLoading: currentLoading } =
    useCurrentSystemVersionQuery();
  const { data: versions = [], isLoading: listLoading } =
    useSystemVersionsQuery();

  const versionsList = Array.isArray(versions) ? versions : [];

  const createMutation = useCreateSystemVersionMutation();
  const updateMutation = useUpdateSystemVersionMutation();
  const deleteMutation = useDeleteSystemVersionMutation();
  const migrateMutation = useRunMigrationsMutation();
  const seedMutation = useRunSeedsMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SystemVersion | null>(null);

  const loading = currentLoading || listLoading;
  const current = currentVersionData?.current ?? null;
  const lastMigrationAt = currentVersionData?.last_migration_at ?? null;
  const lastSeedAt = currentVersionData?.last_seed_at ?? null;
  const beVersion = currentVersionData?.app_version ?? null;

  const versionsMatched = beVersion != null && APP_VERSION === beVersion;

  const handleMigrate = async () => {
    try {
      const res = await migrateMutation.mutateAsync();
      if (res.success) {
        message.success(res.message || "Migrations executed successfully.");
      } else {
        message.error(res.message || "Migration failed.");
      }
    } catch {
      message.error("Migration request failed.");
    }
  };

  const handleSeed = async () => {
    try {
      const res = await seedMutation.mutateAsync();
      if (res.success) {
        message.success(res.message || "Seeds executed successfully.");
      } else {
        message.error(res.message || "Seeding failed.");
      }
    } catch {
      message.error("Seed request failed.");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (record: SystemVersion) => {
    setEditing(record);
    setModalOpen(true);
  };

  const handleSave = async (payload: { version: string; notes?: string }) => {
    try {
      const res = editing
        ? await updateMutation.mutateAsync({ id: editing.id, ...payload })
        : await createMutation.mutateAsync(payload);

      if (res.success) {
        message.success(res.message || "Saved successfully.");
        setModalOpen(false);
      } else {
        message.error(res.message || "Failed to save version.");
      }
    } catch {
      message.error("Save request failed.");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await deleteMutation.mutateAsync(id);
      if (res.success) {
        message.success(res.message || "Deleted successfully.");
      } else {
        message.error(res.message || "Failed to delete version.");
      }
    } catch {
      message.error("Delete request failed.");
    }
  };

  return (
    <Drawer
      title="System Version"
      width={720}
      open={open}
      onClose={onClose}
      extra={
        <Button type="primary" onClick={openCreate}>
          Add Version
        </Button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Spin size="large" />
        </div>
      ) : (
        <div className="space-y-5">
          <VersionOverviewCard
            beVersion={beVersion}
            versionsMatched={versionsMatched}
          />

          <CurrentVersionCard
            current={current}
            lastMigrationAt={lastMigrationAt}
            lastSeedAt={lastSeedAt}
            beVersion={beVersion}
            migrating={migrateMutation.isPending}
            seeding={seedMutation.isPending}
            onMigrate={handleMigrate}
            onSeed={handleSeed}
            formatDateTime={formatDateTime}
          />

          <VersionHistoryTable
            versions={versionsList}
            current={current}
            onEdit={openEdit}
            onDelete={handleDelete}
            formatDateTime={formatDateTime}
          />
        </div>
      )}

      <VersionModal
        open={modalOpen}
        editing={editing}
        saving={createMutation.isPending || updateMutation.isPending}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </Drawer>
  );
};
